import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckbox, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useSearchParams } from 'react-router-dom';
import { requirementSource, useComplianceCoverage } from '../../../hooks/useComplianceCoverage';
import { useProject } from '../../../hooks/useProjects';
import { CoverageInspector } from './CoverageInspector';
export function ComplianceMatrixContainer({ projectId }) {
    const coverage = useComplianceCoverage(projectId);
    const project = useProject(projectId);
    const [params, setParams] = useSearchParams();
    const search = params.get('coverageSearch') ?? '';
    const filter = params.get('coverageState') ?? 'all';
    const history = params.get('coverageHistory') === 'true';
    const selected = params.get('coverageItem');
    const change = (key, value)=>{
        setParams((old)=>{
            const next = new URLSearchParams(old);
            if (value) next.set(key, value);
            else next.delete(key);
            next.set('complianceTab', 'matrix');
            return next;
        }, // Commit query controls before another input/drawer event reads their handlers.
        {
            replace: true,
            flushSync: true
        });
    };
    // Never render cached rows while a fresh tenant/project read is pending or failed.
    if (coverage.isPending || !coverage.isFetchedAfterMount) return /*#__PURE__*/ _jsx("output", {
        children: "Loading current-source coverage…"
    });
    if (coverage.isError) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        children: [
            "Unable to load compliance coverage.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>coverage.refetch(),
                children: "Reload coverage"
            })
        ]
    });
    const data = coverage.data;
    const current = data.items.filter((row)=>!row.superseded && row.versionId === data.currentVersionId);
    const mandatory = current.filter((row)=>requirementSource(row).IsMandatory);
    const addressed = mandatory.filter((row)=>row.substantiveState === 'addressed').length;
    const rows = (history ? data.items : current).filter((row)=>{
        const source = requirementSource(row);
        return `${row.requirementId} ${source.Text} ${source.SourceExactText}`.toLowerCase().includes(search.toLowerCase()) && (filter === 'all' || row.substantiveState === filter);
    });
    const item = rows.find((row)=>`${row.versionId}:${row.requirementId}` === selected);
    const canEdit = project.data?.role === 'owner' || project.data?.role === 'member';
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4 text-foreground",
        "data-testid": Testids.complianceCoverage.matrix,
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "font-semibold",
                children: "Current solicitation compliance"
            }),
            /*#__PURE__*/ _jsxs("p", {
                "data-testid": Testids.complianceCoverage.currentVersion,
                children: [
                    "CURRENT source version: ",
                    data.currentVersionId || 'None issued'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    addressed,
                    " of ",
                    mandatory.length,
                    " mandatory requirements substantively addressed"
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Mapping and draft completion do not confirm substantive coverage. Partial items remain open."
            }),
            !data.currentVersionId && /*#__PURE__*/ _jsx("p", {
                children: "Issue a solicitation in Sources to establish the current basis. No issued source does not mean no requirements."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Search requirements",
                        value: search,
                        onChange: (event)=>change('coverageSearch', event.target.value)
                    }),
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Coverage filter",
                        value: filter,
                        onChange: (event)=>change('coverageState', event.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "all",
                                children: "All coverage states"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: "unaddressed",
                                children: "Uncovered"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: "partial",
                                children: "Partial"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: "addressed",
                                children: "Addressed"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraCheckbox, {
                        id: "coverage-history",
                        checked: history,
                        onCheckedChange: (checked)=>change('coverageHistory', checked === true ? 'true' : ''),
                        "aria-label": "Show superseded history"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: "Show superseded history"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>coverage.refetch(),
                        disabled: coverage.isFetching,
                        children: "Reload coverage"
                    })
                ]
            }),
            rows.length === 0 && /*#__PURE__*/ _jsx("p", {
                children: "No requirements match. Check Sources and extraction; this is not a clean compliance result."
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-3",
                children: rows.map((row)=>{
                    const source = requirementSource(row);
                    const superseded = row.superseded || row.versionId !== data.currentVersionId;
                    return /*#__PURE__*/ _jsxs("li", {
                        className: "space-y-2 rounded border border-border bg-card p-3",
                        "data-testid": Testids.complianceCoverage.row,
                        "data-requirement-id": row.requirementId,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("strong", {
                                        children: row.requirementId
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: row.substantiveState === 'addressed' ? 'success' : 'warning',
                                        children: row.substantiveState.toUpperCase()
                                    }),
                                    source.IsMandatory && /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "danger",
                                        children: "Mandatory"
                                    }),
                                    superseded && /*#__PURE__*/ _jsxs(SpecraBadge, {
                                        variant: "neutral",
                                        children: [
                                            "Superseded · ",
                                            row.versionId
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: source.Text || source.SourceExactText
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    row.locations.length,
                                    " mapped ·",
                                    ' ',
                                    row.locations.filter((location)=>location.draftComplete).length,
                                    " drafted"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Source: ",
                                    source.SourceFilename || 'Solicitation',
                                    " · §",
                                    ' ',
                                    source.SourceSectionNumber || 'unspecified',
                                    " · page",
                                    ' ',
                                    source.SourcePageNumber || 'unspecified',
                                    " ·",
                                    ' ',
                                    source.ObligationStrength || 'Obligation unspecified'
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "aria-label": `Inspect ${row.requirementId}`,
                                onClick: ()=>change('coverageItem', `${row.versionId}:${row.requirementId}`),
                                children: "Inspect requirement"
                            })
                        ]
                    }, `${row.versionId}:${row.requirementId}`);
                })
            }),
            item && /*#__PURE__*/ _jsx(CoverageInspector, {
                projectId: projectId,
                item: item,
                currentVersionId: data.currentVersionId,
                confirmations: data.confirmations,
                members: project.data?.members ?? [],
                canEdit: canEdit && !coverage.isFetching,
                mapping: coverage.mapping,
                confirmation: coverage.confirmation,
                onClose: ()=>change('coverageItem', '')
            }, `${coverage.principal}:${item.versionId}:${item.requirementId}:${item.revision}`)
        ]
    });
}
